import { signinState, kioskState, kioskPairState } from "../../base.routes";
import _ from "lodash";
import { BAD_REQUEST, UNAUTHORIZED } from "../common/common.constants";
import { addBreadcrumb, captureException } from "@sentry/browser";
import promisePoller, { CANCEL_TOKEN } from "promise-poller";

export class AuthService {
    /* @ngInject */
    constructor(
        $http,
        localStorageService,
        $state,
        $cookies,
        PermPermissionStore,
        $q,
        jwtHelper,
        $location,
        $window,
        $urlService,
        OneSignalService,
        KioskService,
    ) {
        this.$http = $http;
        this.$state = $state;
        this.$location = $location;
        this.endpoint = process.env.API_DOMAIN + "/api/auth";
        this.localStorageService = localStorageService;
        this.authRequestConfig = { skipAuthorization: true };
        this.$cookies = $cookies;
        this.PermPermissionStore = PermPermissionStore;
        this.$q = $q;
        this.jwtHelper = jwtHelper;
        this.$window = $window;
        this.loggedInFlagCookieName = "isLoggedIn";
        this.refreshTokenKey = "refresh";
        this.urlAuthTokenKey = "url_auth_token";
        this.urlService = $urlService;
        this.OneSignalService = OneSignalService;
        this.KioskService = KioskService;
        this.authenticationGeneration = 0;
        this.ignoreStates = [
            "signin",
            "signin.done",
            "cookies",
            "reset",
            kioskState.name,
            kioskPairState.name,
        ];
        this.offsetSeconds = 30; // Needed for lag between when a token is considered expired and when the server can reasonably respond
        this.allowUnauthenticatedStates = [
            "reset",
            "takeProfilePicture",
            "visatrace",
            "setupCompanyAccount",
            "publicListTraining",
            "publicListTraining.publicTraining",
            "publicSprayLog",
            "publicSprayLog.applicationRecord",
            "sds.grower",
        ];
        this.inMemoryAccessToken = null;
    }

    redirectToLogin() {
        let svc = this;
        let urlParts = svc.urlService.parts();
        let matched = svc.urlService.match(urlParts);
        let matchedStateName = _.has(matched, "rule.state.name");
        let params = {};

        if (
            matchedStateName &&
            !_.includes(svc.ignoreStates, matched.rule.state.name)
        ) {
            let pathOnly = svc.urlService.path();
            let paramsObj = svc.urlService.search();
            let qs = svc.buildQueryString(paramsObj);
            let fullUrl = qs ? `${pathOnly}?${qs}` : pathOnly;
            params.redirect_to = fullUrl;
        }

        try {
            params.mobile_email = svc.jwtHelper.decodeToken(
                svc.$location.search()[svc.urlAuthTokenKey],
            ).mobile_email;
        } catch {}

        if (
            matchedStateName &&
            !_.includes(
                _.concat(svc.allowUnauthenticatedStates, svc.ignoreStates),
                matched.rule.state.name,
            )
        ) {
            svc.$state.go(signinState, params);
        }
    }

    buildQueryString(paramsObj) {
        return Object.entries(paramsObj)
            .map(
                ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(v)}`,
            )
            .join("&");
    }

    clearStorage() {
        let svc = this;
        svc.authenticationGeneration++;
        svc.refreshingAccessTokenPromise = undefined;
        addBreadcrumb({
            category: "auth",
            message: "Clearing storage",
        });
        svc.setAccessToken(null);
        svc.localStorageService.remove(svc.refreshTokenKey);
        svc.$cookies.remove(svc.loggedInFlagCookieName, {
            path: "/",
            domain: process.env.ROOT_DOMAIN,
        });
        svc.PermPermissionStore.clearStore();
    }

    logout() {
        let svc = this;
        svc.KioskService.stopInactivityLogout();
        svc.clearStorage();
        svc.OneSignalService.logout();
        if (svc.KioskService.getKioskId() && !svc.KioskService.isKioskPage()) {
            return svc.KioskService.returnToKiosk();
        } else {
            svc.redirectToLogin();
        }
    }

    checkAccessToken() {
        let svc = this;
        let accessToken = svc.retrieveAccessToken();
        if (!accessToken) {
            addBreadcrumb({
                category: "auth",
                message: "No access token found",
            });
            return svc.$q.reject("No access token found");
        } else if (
            svc.jwtHelper.isTokenExpired(accessToken, svc.offsetSeconds)
        ) {
            addBreadcrumb({
                category: "auth",
                message: "Access token expired",
            });
            return svc.$q.reject("Access token expired");
        }
        addBreadcrumb({
            category: "auth",
            message: "Access token is good",
        });
        return svc.$q.resolve(accessToken);
    }

    filterValidTokens(tokens) {
        let svc = this;
        return tokens.filter(function (token) {
            try {
                return !svc.jwtHelper.isTokenExpired(token, svc.offsetSeconds);
            } catch (e) {
                addBreadcrumb({
                    category: "auth",
                    message:
                        "Token value caused exception during valid filtering",
                    data: e,
                });
                return false;
            }
        });
    }

    start() {
        let svc = this;
        if (svc.KioskService.isKioskPage()) {
            svc.clearStorage();
            return svc.$q.reject("Kiosk page uses device authentication");
        }
        let urlToken = svc.getTokenFromURL();
        let currentToken = svc.retrieveRefreshToken();
        let foundTokens = [urlToken, currentToken];
        let validTokens = svc.filterValidTokens(foundTokens);
        return svc.pickToken(validTokens).then(function (token) {
            if (
                !svc.KioskService.startInactivityLogout(function () {
                    svc.logout();
                })
            ) {
                return svc.$q.reject("Kiosk inactivity timeout");
            }
            return token;
        });
    }

    pickToken(validTokens) {
        let svc = this;
        let token = validTokens[0];
        if (token) {
            return svc.$q.resolve(token);
        }
        return svc.$q.reject("No valid token available in url or storage");
    }

    getAuthenticatedUserSid(token) {
        let svc = this;
        if (!token) {
            token = svc.retrieveRefreshToken();
        }
        try {
            let userSid = svc.jwtHelper.decodeToken(token).user_sid;
            return svc.$q.resolve(userSid);
        } catch (error) {
            return svc.$q.reject("Unable to get current user sid");
        }
    }

    tokenGetter() {
        let svc = this;
        let authenticationGeneration = svc.authenticationGeneration;
        return svc
            .checkAccessToken()
            .catch(function (reason) {
                addBreadcrumb({
                    category: "auth",
                    message: reason,
                });
                if (svc.refreshingAccessTokenPromise) {
                    addBreadcrumb({
                        category: "auth",
                        message: "Access token refresh request in-progress...",
                    });
                    return svc.refreshingAccessTokenPromise;
                }

                let refresh_token = svc.retrieveRefreshToken();
                addBreadcrumb({
                    category: "auth",
                    message: "Initiating access token refresh request",
                });
                svc.refreshingAccessTokenPromise = promisePoller({
                    taskFn: function () {
                        if (
                            svc.authenticationGeneration !==
                            authenticationGeneration
                        ) {
                            return svc.$q.reject(CANCEL_TOKEN);
                        }
                        return svc.$http
                            .post(
                                svc.endpoint + "/refresh/",
                                { refresh: refresh_token },
                                svc.authRequestConfig,
                            )
                            .then(
                                function (response) {
                                    if (
                                        svc.authenticationGeneration !==
                                        authenticationGeneration
                                    ) {
                                        return svc.$q.reject(CANCEL_TOKEN);
                                    }
                                    let data = response.data;
                                    addBreadcrumb({
                                        category: "auth",
                                        message:
                                            "Successfully refreshed! Setting tokens",
                                    });
                                    svc.setTokens(data.access, data.refresh);
                                    svc.setLoggedInFlag();
                                },
                                function (response) {
                                    addBreadcrumb({
                                        category: "auth",
                                        message:
                                            "Received an error when refreshing access token",
                                        data: {
                                            status: response.status,
                                        },
                                    });
                                    let rejectionValue = {
                                        status: response.status,
                                    };
                                    if (
                                        response.status === UNAUTHORIZED ||
                                        response.status === BAD_REQUEST
                                    ) {
                                        rejectionValue = CANCEL_TOKEN;
                                    }
                                    return svc.$q.reject(rejectionValue);
                                },
                            );
                    },
                });
                return svc.refreshingAccessTokenPromise;
            })
            .then(
                function () {
                    if (
                        svc.authenticationGeneration !==
                        authenticationGeneration
                    ) {
                        return svc.$q.reject({});
                    }
                    svc.refreshingAccessTokenPromise = undefined;
                    return svc.retrieveAccessToken();
                },
                function (reasons) {
                    if (
                        svc.authenticationGeneration !==
                        authenticationGeneration
                    ) {
                        return svc.$q.reject({});
                    }
                    console.log(
                        "Unable to refresh access token. Logging out...",
                    );
                    captureException(
                        "Unable to refresh access token. Logging out...",
                        {
                            contexts: {
                                error: {
                                    attempts: reasons.length,
                                    last_status: _.get(
                                        _.last(reasons),
                                        "status",
                                    ),
                                },
                            },
                        },
                    );
                    svc.logout();
                    svc.refreshingAccessTokenPromise = undefined;
                    return svc.$q.reject({}); // return a rejection to prevent the request from being attempted
                },
            );
    }

    retrieveAccessToken() {
        let svc = this;
        let token = svc.inMemoryAccessToken;
        addBreadcrumb({
            category: "auth",
            message: "Access token retrieved from memory",
        });
        return token;
    }

    retrieveRefreshToken() {
        let svc = this;
        let token = svc.retrieveToken(svc.refreshTokenKey);
        addBreadcrumb({
            category: "auth",
            message: "Refresh token retrieved from storage",
        });
        return token;
    }

    retrieveToken(key) {
        let svc = this;
        return svc.localStorageService.get(key);
    }

    setTokens(accessToken, refreshToken) {
        let svc = this;
        svc.setAccessToken(accessToken);
        svc.setRefreshToken(refreshToken);
    }

    setAccessToken(token) {
        let svc = this;
        addBreadcrumb({
            category: "auth",
            message: "Setting inMemoryAccessToken token",
        });
        svc.inMemoryAccessToken = token;
    }

    setRefreshToken(token) {
        let svc = this;
        svc.setToken(token, svc.refreshTokenKey);
    }

    setToken(token, key) {
        let svc = this;
        addBreadcrumb({
            category: "auth",
            message: "Setting " + key + " token",
        });
        svc.localStorageService.set(key, token);
    }

    setLoggedInFlag() {
        let svc = this;
        svc.$cookies.put(svc.loggedInFlagCookieName, true, {
            domain: process.env.ROOT_DOMAIN,
            samesite: "lax",
            secure: true,
        });
    }

    requestPasswordlessLogin(credentials) {
        let svc = this;
        return svc.$http.post(
            svc.endpoint + "/signin/",
            credentials,
            svc.authRequestConfig,
        );
    }

    getTokenFromURL() {
        let svc = this;
        let token = svc.$location.search()[svc.urlAuthTokenKey];
        svc.$location.search(svc.urlAuthTokenKey, null);
        svc.$location.replace();
        addBreadcrumb({
            category: "auth",
            message: "Token retrieved from URL",
        });
        return token;
    }

    otpRequestCode(data) {
        let svc = this;
        return svc.$http.post(
            svc.endpoint + "/otp-request/",
            data,
            svc.authRequestConfig,
        );
    }

    otpVerifyCode(data) {
        let svc = this;
        return svc.$http.post(
            svc.endpoint + "/otp-verify/",
            data,
            svc.authRequestConfig,
        );
    }
}
